import {
  CurrentOrganizationQuery,
  OrganizationQuery,
  UnreadItemsQuery,
  UnreadItemsUpdatedSubscription,
  UpdateOrganizationMutation,
} from "#src/api/Organization.ts";
import {
  CurrentUserQuery,
  UnattachedProfilesQuery,
  UserBillingSummaryQuery,
  UserQuery,
} from "#src/api/User.ts";
import { useHistory, useLocation } from "#src/components/Router.tsx";
import OrganizationModel from "#src/models/OrganizationModel.ts";
import UserModel from "#src/models/UserModel.ts";
import { apiClient, getPrintedQuery } from "#src/stores/Api.tsx";
import { subscriptionClient } from "#src/stores/Subscription.ts";
import colors from "#src/utils/colors.ts";
import { staffArea } from "#src/utils/constants.ts";
import { moment } from "#src/utils/dateTime.ts";
import { getCurrentHost } from "#src/utils/getCurrentHost.ts";
import {
  getDomain,
  getToken,
  removeToken,
  setToken,
} from "#src/utils/getToken.ts";
import helmet from "#src/utils/helmet.ts";
import { tracker } from "#src/utils/logrocket.ts";
import message from "#src/utils/message.tsx";
import { orgRedirects } from "#src/utils/orgRedirects.ts";
import sentry from "#src/utils/sentry.ts";
import { useSearchParams } from "#src/utils/useSearchParams.ts";
import {
  IntegrationType,
  Level,
  OrganizationStatus,
  UnreadItemsPayload,
  UserBillingSummaryPayload,
  unreadItemsUpdated,
  updateOrganizationVariables,
} from "#types/code-generator.ts";
import Cookies from "js-cookie";
import {
  createContext,
  use,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export interface StoreProps {
  store: StoreType;
}

// counts: { allClasses: number; allOfferings: number } = {
//   allClasses: 0,
//   allOfferings: 0,
// };

type StoreType = ReturnType<typeof useStoreState>;

const defaultUnreadItems: UnreadItemsPayload = {
  allClasses: 0,
  allOfferings: 0,
  customerServiceItems: 0,
  enrolledParticipants: 0,
  inProgressClasses: 0,
  inProgressPrivateSeries: 0,
  inProgressMembershipOfferings: 0,
  allMembershipOfferings: 0,
  passOfferings: 0,
  pendingGroupRegistrations: 0,
  pendingInvoiceItems: 0,
  pendingMemberships: 0,
  pendingPrivateSeries: 0,
  postedPrivateSeries: 0,
  upcomingClasses: 0,
  waitlistAvailableClasses: 0,
  now: null,
};

function useStoreState() {
  const { currentHost, isApp, isDemo } = getCurrentHost();
  const [user, setUser] = useState<UserModel>(null);
  const [userLevel, setUserLevel] = useState(null);
  const [superAdmin, setSuperAdmin] = useState<UserModel>(null);
  const [userTimezone] = useState(moment.tz.guess());
  const [organization, setOrganization] = useState<OrganizationModel>(null);
  const [unreadItems, setUnreadItems] =
    useState<UnreadItemsPayload>(defaultUnreadItems);
  const [billingSummary, setBillingSummary] =
    useState<UserBillingSummaryPayload>(null);
  const [ready, setReady] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string>(null);
  const location = useLocation();
  const unreadItemsSubscription = useRef(null);
  const params = useSearchParams();
  const history = useHistory();
  const interval = useRef(null);

  const departmentsData = window?.localStorage?.getItem("departments") || "[]";
  const departments = departmentsData ? JSON.parse(departmentsData) : [];

  // console.log("unreadItems", unreadItems);

  function checkLocalTime({ local, remote }: { local: Date; remote: Date }) {
    const localTime = local.getTime();
    const remoteTime = remote.getTime();
    const diff = (localTime - remoteTime) / 1000;

    if (Math.abs(diff) > 60 * 5) {
      // console.log("diff", diff);
      moment.now = () => remoteTime;
    }
  }

  async function checkOrganization() {
    if (Object.keys(orgRedirects).includes(currentHost)) {
      let url = `https://${orgRedirects[currentHost]}.captyn.com${location.pathname}${location.search}`;

      if (process.env.NODE_ENV === "development") {
        url = `http://${orgRedirects[currentHost]}.local.localhost:3000${location.pathname}${location.search}`;
      }

      if (process.env.NEXT_PUBLIC_STAGE === "demo") {
        url = `https://${orgRedirects[currentHost]}.nytpac.com${location.pathname}${location.search}`;
      }

      window.location.assign(url);
      return;
    }

    try {
      const currentOrganizationQuery = await apiClient(
        CurrentOrganizationQuery,
        {
          variables: {
            subdomain: currentHost,
          },
          staleTime: 0,
        }
      );

      if (currentOrganizationQuery.organization) {
        const currentOrganization = new OrganizationModel({
          ...currentOrganizationQuery.organization,
          id: isApp ? "app" : currentOrganizationQuery.organization.id,
        });

        setOrganization(currentOrganization);

        checkLocalTime({
          local: new Date(),
          remote: new Date(currentOrganizationQuery.organization.now),
        });

        moment.tz.setDefault(currentOrganization.timezone);
        helmet.setOrganization(currentOrganization);

        console.log("organization", currentOrganization);

        if (currentOrganization.status === OrganizationStatus.Pending) {
          setIsPending(true);
        }
      }
    } catch (e) {
      console.log("currentOrganization error", e);
      setReady(true);
      setError(`Error fetching Organization information.`);
      sentry.captureException(e);
    }
  }

  function boot() {
    if (location.pathname === "/callback-stripe") {
      setReady(true);
    } else {
      void checkOrganization();
    }
  }

  useEffect(() => {
    // subscribeToVersions();
    boot();

    console.log("currentHost", currentHost);

    // const onPreloadError = () => window.location.reload();
    // window.addEventListener("vite:preloadError", onPreloadError);

    // return () => {
    //   window.removeEventListener("vite:preloadError", onPreloadError);

    //   if (interval.current) {
    //     clearInterval(interval.current);
    //     interval.current = null;
    //   }
    // };
  }, []);

  function setTrackerUser() {
    if (tracker && user?.email && organization) {
      tracker.setUserID(user.email.toLowerCase());
      tracker.setMetadata("name", `${user.firstName} ${user.lastName}`);
      tracker.setMetadata("email", user.email);
      tracker.setMetadata("id", user.id);
      tracker.setMetadata("level", user.level);
      tracker.setMetadata("stage", process.env.NEXT_PUBLIC_STAGE);
      tracker.setMetadata("organization", organization?.subdomain);
    }
  }

  useEffect(() => {
    // run once immediately when data arrives
    setTrackerUser();

    // Only start interval after both are set
    if (!user || !organization) {
      if (interval.current) {
        clearInterval(interval.current);
        interval.current = null;
      }
      return;
    }

    if (interval.current) {
      clearInterval(interval.current);
    }

    interval.current = setInterval(() => {
      setTrackerUser();
    }, 10 * 1000);
  }, [tracker, user, organization]);

  function setCurrentUser({ token = null, user }) {
    // console.log("setCurrentUser token", token);
    // console.log("setCurrentUser user", user);

    const cookieConfig = {
      domain: getDomain(),
    };

    const viewingAs = Cookies.get("viewingAs", cookieConfig);

    if (token && !viewingAs) {
      setToken(token);
    }

    const newUser = new UserModel(user);
    setUser(newUser);

    if (!userLevel) {
      setUserLevel(newUser.level);
    }

    // if (newUser.isAdminOrAbove && organization) {
    //   subscribeToUnreadItems(newUser);
    // }

    setReady(true);
  }

  async function getCurrentUser() {
    const userToken = getToken();

    if (!userToken) {
      return;
    }

    try {
      const currentUserQuery = await apiClient(CurrentUserQuery);

      // console.log("currentUserQuery", currentUserQuery);

      if (currentUserQuery.me) {
        const user = currentUserQuery.me.user;
        setCurrentUser({ user, token: currentUserQuery.me.token });
      } else {
        signOut();
        setReady(true);
      }
    } catch (e) {
      message.error(e);
      setReady(true);
    }
  }

  async function checkCurrentUser() {
    let userToken = getToken();

    if (params.has("token")) {
      userToken = params.get("token");
      setToken(userToken);
      params.delete("token");
      history.replace(`?${params.toString()}`);
    }

    if (userToken) {
      void getCurrentUser();
    } else {
      setReady(true);
    }
  }

  useEffect(() => {
    if (organization && !user) {
      void checkCurrentUser();
    }
  }, [organization]);

  async function processCurrentUser() {
    if (!user) {
      return;
    }

    if (!isApp) {
      void getUserBillingSummary();
    }

    if (
      Cookies.get("viewingAs") &&
      Cookies.get("viewingAsToken") &&
      Cookies.get("accessToken")
    ) {
      const realUserQuery = await apiClient(CurrentUserQuery, {
        variables: {},
        token: Cookies.get("viewingAsToken"),
      });

      setSuperAdmin(new UserModel(realUserQuery.me.user));
    }

    if (user.level === Level.Kiosk && !sessionStorage.getItem("kiosk")) {
      signOut();
    }

    if (!user.email?.includes("email.fake")) {
      setTrackerUser();
    }
  }

  useEffect(() => {
    void processCurrentUser();
  }, [user]);

  async function getUserBillingSummary() {
    if (!user || user?.level === Level.Kiosk || user?.isSalesOrAbove) {
      return;
    }

    const summary = await apiClient(UserBillingSummaryQuery, {
      variables: {
        id: user.id,
      },
    });

    // console.log("summary", summary);

    setBillingSummary(summary?.userBillingSummary);
  }

  function checkForUnattachedProfiles() {
    return apiClient(UnattachedProfilesQuery, {
      variables: {
        email: user.email,
      },
    })
      .then((res) => res.users)
      .catch((e) => console.log("checkForUnattachedProfiles error", e));
  }

  async function updateUser() {
    if (!user) {
      return;
    }

    const userQuery = apiClient(UserQuery, {
      variables: {
        id: user.id,
      },
    });

    return userQuery
      .then((res) => {
        // console.log("res", res);
        if (res.user) {
          const user = res.user;
          setCurrentUser({ user, token: null });
        }
      })
      .catch((e) => {
        console.log("updateUser error", e);
      });
  }

  function signOut() {
    // console.log("signout");
    removeToken();

    setTimeout(() => {
      setUser(null);
      setUserLevel(null);
      window?.localStorage?.removeItem("departments");
    }, 16);
  }

  function subscribeToUnreadItems(newUser = null) {
    let currentUser = user;

    if (newUser) {
      currentUser = newUser;
    }

    if (!currentUser?.isAdminOrAbove) {
      return;
    }

    if (unreadItemsSubscription.current) {
      unreadItemsSubscription.current();
    }

    unreadItemsSubscription.current =
      subscriptionClient.subscribe<unreadItemsUpdated>(
        {
          query: getPrintedQuery(UnreadItemsUpdatedSubscription),
          variables: {
            organization: organization.id,
            departments: departments.map((d) => d.id),
          },
        },
        {
          next: ({ data }) => {
            // console.log("subscribe data", data);
            // console.log("unreadItems", unreadItems);

            if (data?.unreadItemsUpdated) {
              checkLocalTime({
                local: new Date(),
                remote: new Date(data.unreadItemsUpdated.now),
              });
            }

            setUnreadItems((current) => {
              const newUnreadItems = {
                ...current,
              };

              for (const d in data?.unreadItemsUpdated) {
                const count = data?.unreadItemsUpdated[d];

                if (count !== null) {
                  newUnreadItems[d] = count;
                }
              }

              // console.log("newUnreadItems", newUnreadItems);

              return newUnreadItems;
            });
          },
          error: (_e) => {
            // console.log("unreadItems error", e);
            if (unreadItemsSubscription.current) {
              unreadItemsSubscription.current();
            }
          },
          complete: () => {
            // console.log("complete");
          },
        }
      );

    void apiClient(UnreadItemsQuery, {
      variables: {
        organization: organization.id,
        departments: departments.map((d) => d.id),
      },
    }).then((res) => {
      // console.log("unreadItems res", res);
      if (res.unreadItems) {
        setUnreadItems({
          ...defaultUnreadItems,
          ...res.unreadItems,
        });
      }
    });
  }

  async function updateOrganization(
    variables: updateOrganizationVariables = null
  ) {
    let newOrganization: OrganizationModel;

    if (variables) {
      const updateOrganization = await apiClient(UpdateOrganizationMutation, {
        variables,
      });

      if (updateOrganization.updateOrganization) {
        newOrganization = new OrganizationModel(
          updateOrganization.updateOrganization
        );
      }
    } else {
      const currentOrganization = await apiClient(OrganizationQuery, {
        variables: {
          id: organization.id,
        },
      });

      if (currentOrganization.organization) {
        newOrganization = new OrganizationModel(
          currentOrganization.organization
        );
      }
    }

    setOrganization(newOrganization);

    void apiClient(UnreadItemsQuery, {
      variables: {
        organization: newOrganization.id,
        departments: departments.map((d) => d.id),
      },
    }).then((res) => {
      // console.log("unreadItems res", res);
      if (res.unreadItems) {
        setUnreadItems({
          ...defaultUnreadItems,
          ...res.unreadItems,
        });
      }
    });

    return newOrganization;
  }

  const isAdminArea = useMemo(() => {
    let isAdminArea =
      location.pathname.indexOf("/admin") === 0 ||
      location.pathname === "/start" ||
      location.pathname.indexOf("/start/") === 0;

    // if (user?.isStaff && location.pathname.indexOf("/account") === 0) {
    //   isAdminArea = true;
    // }

    return isAdminArea;
  }, [location]);

  const isAccountArea = useMemo(() => {
    const isAccountArea = location.pathname.indexOf("/account") === 0;

    return isAccountArea;
  }, [location]);

  const isStaffArea = useMemo(() => {
    return staffArea.some((regex) => regex.test(location.pathname));
  }, [location]);

  const isSuperAdminArea = useMemo(() => {
    const isSuperAdminArea = location.pathname.indexOf("/super-admin") === 0;

    return isSuperAdminArea;
  }, [location]);

  const theme = useMemo(() => {
    let orgColor = colors.action;
    let actionColor = colors.action;

    if (organization?.color?.length > 6) {
      orgColor = organization.color;
    }

    const themeData = {
      ...colors,
      orgColor,
      orgOrgColor: orgColor,
      actionColor,
    };

    if (isAdminArea || isSuperAdminArea) {
      themeData.actionColor = colors.action;
      themeData.orgColor = colors.action;

      if (organization?.integrations.includes(IntegrationType.FleetFeet)) {
        console.log("orgColor", orgColor);
        themeData.actionColor = orgColor;
        themeData.orgColor = orgColor;
      }
    }

    document.documentElement.style.setProperty(
      "--organization",
      themeData.orgColor
    );

    // console.log("themeData", themeData);

    return themeData;
  }, [organization, isAdminArea, isSuperAdminArea]);

  const unread = useMemo(() => {
    return (
      unreadItems?.customerServiceItems +
      unreadItems?.pendingGroupRegistrations +
      unreadItems?.pendingInvoiceItems +
      unreadItems?.pendingMemberships +
      unreadItems?.pendingPrivateSeries +
      unreadItems?.waitlistAvailableClasses
    );
  }, [unreadItems]);

  return {
    billingSummary,
    checkForUnattachedProfiles,
    error,
    getCurrentUser,
    isAccountArea,
    isAdminArea,
    isApp,
    isDemo,
    isPending,
    isStaffArea,
    isSuperAdminArea,
    // location,
    organization,
    ready,
    setCurrentUser,
    setOrganization,
    setSuperAdmin,
    setUserLevel,
    signOut,
    subscribeToUnreadItems,
    superAdmin,
    theme,
    unread,
    unreadItems,
    updateOrganization,
    updateUser,
    user,
    userLevel,
    userTimezone,
  };
}

const StoreContext = createContext<StoreType>(null);

export const StoreProvider = (props) => {
  const store = useStoreState();

  return (
    <StoreContext.Provider value={store}>
      {props.children}
    </StoreContext.Provider>
  );
};

export const useStore = () => {
  const store = use(StoreContext);
  return store;
};

export function withStore(Component) {
  return function WithStoreComponent(props) {
    return (
      <StoreContext.Consumer>
        {(storeContext) => <Component {...props} store={storeContext} />}
      </StoreContext.Consumer>
    );
  };
}
